/**
 * `@mui/x-chat`'s built-in texts (composer placeholder, send/attach buttons,
 * conversation list, scroll-to-bottom, status and landmark labels, …) in the
 * app's UI language. The library ships only English (`chatEnUS`), so
 * without this `ChatBox` keeps those texts English whatever language the
 * app shows. Returns a complete `ChatLocaleText` — not a partial override —
 * so none of them can silently fall back to English; the object is rebuilt
 * whenever the language changes (`t`/the formatters change identity with
 * it), which re-renders every `ChatBox` part reading it.
 *
 * Keys are the library's English source strings (namespace "Assistant"),
 * except where the product already has a term for the same thing (the
 * composer placeholder, the empty conversation list, "New conversation",
 * "Assistant") or a matching `Global` term is reused.
 */
import type {
  ChatLocaleText,
  ChatLocaleTypingUser,
  ChatMessageStatus,
  ChatToolInvocationState,
} from "@mui/x-chat/headless";
import { useFormatting } from "@msys/ui";
import { useTolgee, useTranslate } from "@tolgee/react";
import React from "react";

type AssistantT = ReturnType<typeof useTranslate>["t"];

function messageStatusLabel(t: AssistantT, status: ChatMessageStatus): string {
  switch (status) {
    case "pending":
      return t("Pending", { ns: "Assistant" });
    case "sending":
      return t("Sending", { ns: "Assistant" });
    case "streaming":
      return t("Streaming", { ns: "Assistant" });
    case "sent":
      return t("Sent", { ns: "Assistant" });
    case "read":
      return t("Read", { ns: "Assistant" });
    case "error":
      return t("Error", { ns: "Global" });
    case "cancelled":
      return t("Cancelled", { ns: "Assistant" });
  }
}

function toolStateLabel(t: AssistantT, state: ChatToolInvocationState): string {
  switch (state) {
    case "input-streaming":
    case "input-available":
    case "approval-responded":
      return t("Running…", { ns: "Assistant" });
    case "approval-requested":
      return t("Awaiting approval", { ns: "Assistant" });
    case "output-available":
      return t("Completed", { ns: "Global" });
    case "output-error":
      return t("Failed", { ns: "Assistant" });
    case "output-denied":
      return t("Denied", { ns: "Assistant" });
  }
}

function typingUserName(user: ChatLocaleTypingUser): string {
  return user.displayName ?? user.id;
}

export function useAgentChatLocaleText(): ChatLocaleText {
  const { t } = useTranslate(["Assistant", "Global"]);
  const language = useTolgee(["language"]).getLanguage();
  const { getFormattedDate, getFormattedDateTime } = useFormatting();

  return React.useMemo(
    (): ChatLocaleText => ({
      composerInputPlaceholder: t("Ask the assistant…", { ns: "Assistant" }),
      composerInputAriaLabel: t("Message", { ns: "Assistant" }),
      composerSendButtonLabel: t("Send message", { ns: "Assistant" }),
      composerAttachButtonLabel: t("Add attachment", { ns: "Assistant" }),
      composerAttachInputLabel: t("Upload file", { ns: "Assistant" }),
      composerAttachmentFallbackLabel: t("Attachment", { ns: "Assistant" }),
      composerRemoveAttachmentLabel: fileName =>
        t("Remove {fileName}", { ns: "Assistant", fileName }),
      messageCopyButtonLabel: t("Copy", { ns: "Assistant" }),
      messageCopyCodeButtonLabel: t("Copy code", { ns: "Assistant" }),
      messageCopiedCodeButtonLabel: t("Copied", { ns: "Assistant" }),
      messageEditedLabel: t("Edited", { ns: "Assistant" }),
      messageDeletedLabel: t("Deleted", { ns: "Assistant" }),
      messageReasoningLabel: t("Reasoning", { ns: "Assistant" }),
      messageReasoningStreamingLabel: t("Thinking…", { ns: "Assistant" }),
      messageToolInputLabel: t("Tool called", { ns: "Assistant" }),
      messageToolOutputLabel: t("Tool result", { ns: "Assistant" }),
      messageToolApproveButtonLabel: t("Approve", { ns: "Assistant" }),
      messageToolDenyButtonLabel: t("Deny", { ns: "Assistant" }),
      conversationListNoConversationsLabel: t("No earlier conversations", {
        ns: "Assistant",
      }),
      conversationListSearchPlaceholder: t("Search conversations", {
        ns: "Assistant",
      }),
      unreadMarkerLabel: t("New messages", { ns: "Assistant" }),
      retryButtonLabel: t("Retry", { ns: "Global" }),
      reconnectButtonLabel: t("Reconnect", { ns: "Assistant" }),
      scrollToBottomLabel: t("Scroll to bottom", { ns: "Assistant" }),
      threadNoMessagesLabel: t("No messages yet", { ns: "Assistant" }),
      threadNoMessagesHelperText: t("Type a message to get started", {
        ns: "Assistant",
      }),
      genericErrorLabel: t("Something went wrong", { ns: "Assistant" }),
      loadingLabel: t("Loading…", { ns: "Global" }),
      messageStatusLabel: status => messageStatusLabel(t, status),
      toolStateLabel: state => toolStateLabel(t, state),
      messageTimestampLabel: dateTime => getFormattedDateTime(dateTime),
      conversationTimestampLabel: dateTime => getFormattedDate(dateTime),
      typingIndicatorLabel: users => {
        const names = users.map(typingUserName).join(", ");
        return users.length === 1
          ? t("{names} is typing", { ns: "Assistant", names })
          : t("{names} are typing", { ns: "Assistant", names });
      },
      scrollToBottomWithCountLabel: count =>
        t("Scroll to bottom, {count} new messages", {
          ns: "Assistant",
          count,
        }),
      suggestionsLabel: t("Suggested prompts", { ns: "Assistant" }),
      messageListLabel: t("Message log", { ns: "Assistant" }),
      messageLabel: t("Message", { ns: "Assistant" }),
      conversationHeaderMenuLabel: t("Open conversations", {
        ns: "Assistant",
      }),
      conversationHeaderBackLabel: t("Back to conversations", {
        ns: "Assistant",
      }),
      conversationHeaderCloseLabel: t("Close conversations", {
        ns: "Assistant",
      }),
      conversationHeaderNewChatLabel: t("New conversation", {
        ns: "Assistant",
      }),
      conversationHeaderSettingsLabel: t("Settings", { ns: "Global" }),
      messageAuthorUserLabel: t("User", { ns: "Global" }),
      messageAuthorAssistantLabel: t("Assistant", { ns: "Assistant" }),
      messageAuthorSystemLabel: t("System", { ns: "Global" }),
      conversationListLandmarkLabel: t("Conversations", { ns: "Assistant" }),
      threadLandmarkLabel: t("Conversation", { ns: "Assistant" }),
      composerLandmarkLabel: t("Message composer", { ns: "Assistant" }),
      messageActionsLabel: t("Message actions", { ns: "Assistant" }),
      responseStreamingStartedAnnouncement: t("Assistant is responding", {
        ns: "Assistant",
      }),
      responseStreamingCompletedAnnouncement: t("Response complete", {
        ns: "Assistant",
      }),
    }),
    // `language` is listed on purpose: the texts must be rebuilt on every
    // switch even if `t` kept its identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [language, t, getFormattedDate, getFormattedDateTime]
  );
}
